
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useAuth } from "@/providers/AuthProvider";
import { Loader2 } from "lucide-react";

const UserProfile = () => {
  const { user, signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fullName, setFullName] = useState("");
  
  // Initialize fullName from user metadata when it's available
  useEffect(() => {
    if (user?.user_metadata?.full_name) {
      setFullName(user.user_metadata.full_name);
    } else if (user?.user_metadata?.name) {
      // For Google auth, the name field is used instead of full_name
      setFullName(user.user_metadata.name);
    }
  }, [user]); // Re-run when user changes

  const handleSignOut = async () => {
    setLoading(true);
    try {
      await signOut();
    } catch (error) {
      console.error("Error in sign out:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Get initials for avatar fallback
  const getInitials = () => {
    if (fullName) {
      return fullName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    }
    
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    
    return "US";
  };

  if (!user) return null;

  return (
    <div className="flex flex-col items-center space-y-4">
      <Avatar className="h-20 w-20">
        <AvatarImage src={user.user_metadata?.avatar_url || user.user_metadata?.picture || ""} />
        <AvatarFallback className="bg-heaven-gold text-white text-xl">
          {getInitials()}
        </AvatarFallback>
      </Avatar>
      
      <div className="text-center">
        <h3 className="text-xl font-medium">{fullName || "User"}</h3>
        <p className="text-sm text-gray-500">{user.email}</p>
      </div>
      
      <Button 
        variant="outline" 
        className="mt-4" 
        onClick={handleSignOut}
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Signing out...
          </>
        ) : "Sign out"}
      </Button>
    </div>
  );
};

export default UserProfile;
